import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import VectorSource from "ol/source/Vector";
import Cluster from "ol/source/Cluster";
import VectorImageLayer from "ol/layer/VectorImage";
import VectorLayer from "ol/layer/Vector";
import { useRouteMatch } from "react-router-dom";
import Map from "ol/Map";
import View from "ol/View";
import * as utils from "./utils";
import {
  createEmpty as createEmptyExtent,
  extend as extendExtent
} from "ol/extent";
import TileLayer from "ol/layer/Tile";
import XYZ from "ol/source/XYZ";
import olMapboxStyleApply from "ol-mapbox-style";
import countryLocations from "../data/locations.json";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { MAP_USE_RASTER, routeColors } from "../data/constants";
import { defaults as defaultControls } from "ol/control";
import Polygon from "ol/geom/Polygon";
import ConvexHull from "ol-ext/geom/ConvexHull";
import Hover from "ol-ext/interaction/Hover";
import { defaults as defaultInteractions } from "ol/interaction";
import { mergeReloadStopsToMainRoute } from "../../utils/helpers";
import LineString from "ol/geom/LineString";
import Popup from "ol-ext/overlay/Popup";
import { DEVICE_PIXEL_RATIO } from "ol/has";
import { secondsToTime, parseDateStr } from "../../utils/helpers";
import SelectClusterInteraction from "ol-ext/interaction/SelectCluster";
import * as OLEventsCondition from "ol/events/condition";

// Max zoom level to have clustering
const CLUSTER_MAX_ZOOM_LEVEL = 13.5;
// Distance between markers to cluster them in pixels
const CLUSTERING_DISTANCE = 200;
// Internal ref state initial value
const internalStoreInitialValue = {
  saveDisabled: "",
  currentEditId: "",
  showingRouteId: "",
  hoveredRouteId: null,
  markersHidden: false,
  unscheduledMarkersHidden: false,
  snapToRoadEnabled: false,
  markersClustered: false,
  currentRefitHash: ""
};

const useMapHooks = props => {
  const {
    organisation,
    projectId,
    solution,
    setMapCenter,
    setMapBounds,
    tilesDark,
    plan,
    markersClustered,
    markersHidden,
    unscheduledMarkersHidden,
    editId,
    setExtendableId,
    setExtendableType,
    toggleDrawer,
    saveDisabled,
    isReloadAllowed,
    fetchRoutes,
    polylines,
    services,
    setShowingRouteId,
    showingRouteId,
    snapToRoadEnabled,
    publishedOnce,
    driverLocations,
    vehicles,
    hoveredStop,
    hoveredRouteId,
    lockedMatchStopId,
    refitHash
  } = props;

  // General
  const match = useRouteMatch("/:projectId");

  // States
  // if map and layers created
  const [mapCreated, setMapCreated] = useState(false);
  // If map initialized with default settings after creation.
  const [mapInitialized, setMapInitialized] = useState(false);
  // If organization loaded and map initialized with the data needed from it.
  const [organisationLoaded, setOrganisationLoaded] = useState(false);
  // Changes dialog state
  const [changesDialog, setChangesDialog] = useState({
    visible: false,
    id: null
  });
  // Driver with opened popup
  const [driverTrackRouteId, setDriverTrackRouteId] = useState(null);

  // Refs
  // Reference to OpenLayers map instance.
  const mapRef = useRef(null);
  // Reference to stops marker layer
  const stopMarkersLayerRef = useRef(null);
  /*
    Reference to current hovered cluster marker. As we use cluster for markers
    the main feature is a cluster feature, and other features are part of this
    cluster feature.
   */
  const currentHoveredStopMarkersLayerFeatureRef = useRef(null);
  /*
    Reference to current hovered marker in showing route layer
   */
  const currentHoveredShowingRouteMarkersLayerFeatureRef = useRef(null);
  // The layer to hold cluster hover convex hull surface
  const clusterSurfaceLayerRef = useRef(null);
  // The layer to hold features text on hover
  const featureTextLayerRef = useRef(null);
  // The layer to hold features text on hover
  const routePolylinesLayerRef = useRef(null);
  // The route that is showing route markers layer
  const showingRouteMarkersLayerRef = useRef(null);
  // The route that is showing route polyline layer
  const showingRoutePolylineLayerRef = useRef(null);
  // The Snap to road layer
  const snapToRoadLayerRef = useRef(null);
  // The layer to hold edit marker
  const editMarkerLayerRef = useRef(null);
  // The layer to hold hovered marker
  const hoveredMarkerLayerRef = useRef(null);
  // The layer to hold the pinned (locked RouteTag) stop marker
  const lockedMarkerLayerRef = useRef(null);
  // The layer to hold drivers marker
  const driversLayerRef = useRef(null);
  // The popup overlay ref
  const popupOverlayRef = useRef(null);
  // Internal store
  const internalStoreRef = useRef(internalStoreInitialValue);
  // Matched project ref
  const matchedProjectRef = useRef(
    match && match.params && match.params.projectId
  );

  // Memos
  /*
    Get normalized routes, which is the routes that all routes with reload are
    merged into one big route. Also, any route without any stop is filtered.
   */
  const normalizedRoutes = useMemo(() => {
    let theRoutes = [];
    if (solution && solution.routes && solution.status !== "ERROR") {
      if (isReloadAllowed) {
        theRoutes = mergeReloadStopsToMainRoute(solution.routes);
      } else {
        theRoutes = solution.routes;
      }
    }
    return theRoutes.filter(route => route.stops.length > 2);
  }, [solution, isReloadAllowed]);

  /*
    Normalized routes stops to draw markers on the map, which means removing
    break and reload stops, as we don't show them on the map.
   */
  const normalizedRoutesWithNormalizedStops = useMemo(() => {
    return normalizedRoutes.map(route => ({
      ...route,
      stops: route.stops.filter(
        ({ type }) => type !== "break" && type !== "reload"
      )
    }));
  }, [normalizedRoutes]);

  // Callbacks
  /*
    Update map center and bounds that shown to user in store so other places
    in our app, like stop search inputs use to limit the suggestions to current
    map viewport.
   */
  const updateMapCenterAndBoundsInStore = useCallback(() => {
    setMapCenter(
      utils.pointArray.toLeafletObject(
        utils.pointArray.xyToLonLat(mapRef.current.getView().getCenter())
      )
    );
    setMapBounds(
      utils.extentArray.toLeafletObject(
        utils.extentArray.xyToLonLat(
          mapRef.current.getView().calculateExtent(mapRef.current.getSize())
        )
      )
    );
  }, [setMapBounds, setMapCenter]);

  // Refit the view to the markers extent
  const refitMarkersExtent = useCallback((newMarkers = undefined) => {
    const markers =
      newMarkers ||
      stopMarkersLayerRef.current
        .getSource()
        .getFeatures()
        .concat(showingRouteMarkersLayerRef.current.getSource().getFeatures());
    if (markers.length > 0) {
      const extent = createEmptyExtent();
      markers.forEach(marker => {
        try {
          extendExtent(extent, marker.getGeometry().getExtent());
        } catch (error) {
          console.error("Error getting marker extent", error, markers);
        }
      });
      try {
        mapRef.current
          .getView()
          .fit(extent, { duration: 500, padding: [50, 50, 50, 50] });
      } catch (error) {
        console.error("Error fitting extent to markers", error, markers);
      }
    }
  }, []);

  // Handle the actual click
  const handleClick = useCallback(
    (id, type, status) => {
      if (type !== "start" && type !== "end") {
        toggleDrawer(true);
        setExtendableId(id);
        setExtendableType("stops", { status });
      }
    },
    [setExtendableId, setExtendableType, toggleDrawer]
  );

  // Handle click on marker or polyline respecting unsaved changes
  const warnClick = useCallback(
    (id, type, status) => {
      if (
        type !== "start" &&
        type !== "end" &&
        internalStoreRef.current.currentEditId !== id
      ) {
        if (!internalStoreRef.current.saveDisabled) {
          setChangesDialog({ visible: true, id, type, status });
        } else {
          handleClick(id, type, status);
        }
      }
    },
    [setChangesDialog, handleClick]
  );

  // Get route color
  const getRouteColor = useCallback((colorsObject, routeId) => {
    const colorIndex = colorsObject[routeId] >= 0 ? colorsObject[routeId] : 0;
    return routeColors[colorIndex % routeColors.length];
  }, []);

  // Get route stop markers
  const getRouteStopMarkers = useCallback(
    (routeStops, routeId, routeColors, showingRouteLayer) => {
      const color = getRouteColor(routeColors, routeId);
      const startStop = routeStops.find(({ type }) => type === "start");
      const markers = [];
      routeStops.forEach((stop, index) => {
        if (stop.type === "end") {
          if (
            startStop &&
            startStop.address &&
            stop.address &&
            startStop.address.lat === stop.address.lat &&
            startStop.address.lng === stop.address.lng
          ) {
            return;
          }
        }
        const marker = new Feature({
          geometry: new Point(
            utils.pointArray.lonLatToXY([stop.address.lng, stop.address.lat])
          ),
          markerType: "stop",
          label: stop.information ? stop.information.street : "",
          zIndex: showingRouteLayer
            ? Math.min(551 + Math.random() * 448, 999 + Math.random())
            : Math.min(12 + Math.random() * 487, 499 + Math.random()),
          routeId,
          stopData: {
            id: stop.id,
            type: stop.type,
            status: stop.status,
            lon: stop.address.lng,
            lat: stop.address.lat,
            streetLon: stop.address.lng_str || stop.address.lng || "",
            streetLat: stop.address.lat_str || stop.address.lat || "",
            color,
            index
          }
        });
        marker.setId(stop.id);
        markers.push(marker);
      });
      return markers;
    },
    [getRouteColor]
  );

  // Create the map for the first time.
  const createMap = element => {
    // Layer to hold text on hover of features
    featureTextLayerRef.current = new VectorLayer({
      zIndex: 550,
      source: new VectorSource()
    });

    // Layer to hold the hovered marker
    hoveredMarkerLayerRef.current = new VectorLayer({
      zIndex: 530,
      source: new VectorSource()
    });

    // Layer to hold the pinned (locked RouteTag) stop marker — its own layer so
    // it persists independently of the transient hovered-marker layer.
    lockedMarkerLayerRef.current = new VectorLayer({
      zIndex: 529,
      source: new VectorSource()
    });

    // Layer to hold edit stop marker
    editMarkerLayerRef.current = new VectorLayer({
      zIndex: 520,
      source: new VectorSource()
    });

    // Layer to hold the current drivers location
    driversLayerRef.current = new VectorLayer({
      zIndex: 515,
      source: new VectorSource(),
      name: "driversLayer"
    });

    // Layer to hold showing route markers layer
    showingRouteMarkersLayerRef.current = new VectorLayer({
      zIndex: 510,
      style: feature =>
        utils.layerStyles.getMarkerLayerFeatureStyle(
          feature,
          "normal",
          !(
            internalStoreRef.current.markersClustered &&
            mapRef.current.getView().getZoom() < CLUSTER_MAX_ZOOM_LEVEL
          )
        ),
      source: new Cluster({
        distance: 0,
        minDistance: 50,
        source: new VectorSource()
      }),
      name: "showingRouteMarkerLayer"
    });

    /*
      Stops markers layer that is using cluster source of vector source of
      features.
     */
    stopMarkersLayerRef.current = new VectorImageLayer({
      zIndex: 10,
      style: feature =>
        utils.layerStyles.getMarkerLayerFeatureStyle(
          feature,
          "normal",
          !(
            internalStoreRef.current.markersClustered &&
            mapRef.current.getView().getZoom() < CLUSTER_MAX_ZOOM_LEVEL
          )
        ),
      source: new Cluster({
        distance: CLUSTERING_DISTANCE,
        minDistance: 50,
        source: new VectorSource()
      }),
      name: "stopsMarker"
    });

    // Layer to show cluster convex hull surface in case of hover on cluster
    clusterSurfaceLayerRef.current = new VectorLayer({
      zIndex: 9,
      source: new VectorSource()
    });

    // Layer to hold showing route polyline layer
    showingRoutePolylineLayerRef.current = new VectorLayer({
      zIndex: 8,
      source: new VectorSource(),
      name: "showingRoutePolylineLayer",
      style: (feature, resolution) => {
        // Base polyline style is now [casing, line] (an array) — spread it so the
        // returned style list stays flat: [casing, line, ...directionArrows].
        const base = utils.layerStyles.getShowingRouteLayerFeatureStyle(feature);
        const styles = Array.isArray(base) ? [...base] : [base];

        const size = 128;
        const mapSize = mapRef.current.getSize();
        const extent = mapRef.current
          .getView()
          .calculateExtent([mapSize[0] + size * 2, mapSize[1] + size * 2]);
        const zoomLevel = mapRef.current
          .getView()
          .getZoomForResolution(resolution);

        const splitPoints = utils.lineString.splitLineString(
          feature.getGeometry(),
          size * resolution,
          { midPoints: true, extent: extent, alwaysUp: false }
        );

        splitPoints.forEach(function (point) {
          const style = utils.featureStyles
            .getRoutePolylineDirectionMarker(zoomLevel, feature.get("color"))
            .clone();
          style.getImage().setRotation(point[2]);
          style.setGeometry(new Point([point[0], point[1]]));
          styles.push(style);
        });
        return styles;
      }
    });

    // Layer to hold text on hover of features
    routePolylinesLayerRef.current = new VectorLayer({
      zIndex: 7,
      source: new VectorSource(),
      name: "routePolylines"
    });

    // Snap to road layer
    snapToRoadLayerRef.current = new VectorLayer({
      zIndex: 6,
      source: new VectorSource()
    });

    // Popup overlay
    popupOverlayRef.current = new Popup({
      popupClass: "default",
      closeBox: true,
      positioning: "auto",
      autoPan: true,
      autoPanAnimation: { duration: 250 },
      offsetBox: [0, -65, 0, 0],
      onclose() {
        setDriverTrackRouteId(null);
      }
    });

    // Hover interactions
    const stopMarkersLayerHoverInteraction = new Hover({
      cursor: "pointer",
      layers: [stopMarkersLayerRef.current]
    });

    const showingRouteMarkersLayerHoverInteraction = new Hover({
      cursor: "pointer",
      layers: [showingRouteMarkersLayerRef.current]
    });

    const routeHoverInteraction = new Hover({
      cursor: "pointer",
      layers: [routePolylinesLayerRef.current]
    });

    const driversHoverInteraction = new Hover({
      cursor: "pointer",
      layers: [driversLayerRef.current]
    });

    // Select cluster interaction
    const markersClusterSelectInteraction = new SelectClusterInteraction({
      animate: true,
      pointRadius: 25,
      spiral: true,
      condition: OLEventsCondition.click,
      autoClose: true,
      selectCluster: false,
      featureStyle: utils.layerStyles.getSelectClusterFeatureStyle
    });

    markersClusterSelectInteraction.on("select", event => {
      const selectedFeature = event.selected[0];
      if (markersClusterSelectInteraction.getFeatures().getLength() > 0) {
        const features = selectedFeature.get("features");
        if (features) {
          if (features.length > 1) {
          } else {
            markersClusterSelectInteraction.clear();
            const marker = features[0];
            const { id, type, status } = marker.get("stopData");
            if (type !== "start") {
              warnClick(id, type, status);
            }
          }
        } else {
          markersClusterSelectInteraction.clear();
        }
      }
    });

    // The map
    mapRef.current = new Map({
      target: element,
      view: new View({
        // Set center to amsterdam Dam square :)
        center: utils.pointArray.lonLatToXY([4.8926, 52.3731]),
        zoom: 7,
        maxZoom: 20,
        // Padding to left because of the projects sidebar
        padding: [0, 0, 0, 448]
      }),
      // Config default controls of the map
      controls: defaultControls({
        rotate: false,
        zoom: false,
        attributionOptions: {
          collapsible: false
        }
      }),
      layers: [
        stopMarkersLayerRef.current,
        clusterSurfaceLayerRef.current,
        routePolylinesLayerRef.current,
        snapToRoadLayerRef.current,
        showingRouteMarkersLayerRef.current,
        showingRoutePolylineLayerRef.current,
        driversLayerRef.current,
        editMarkerLayerRef.current,
        hoveredMarkerLayerRef.current,
        lockedMarkerLayerRef.current,
        featureTextLayerRef.current
      ],
      interactions: defaultInteractions({
        altShiftDragRotate: false,
        keyboard: false,
        pinchRotate: false
      }).extend([
        stopMarkersLayerHoverInteraction,
        showingRouteMarkersLayerHoverInteraction,
        routeHoverInteraction,
        driversHoverInteraction,
        markersClusterSelectInteraction
      ]),
      overlays: [popupOverlayRef.current]
    });

    window.map = mapRef.current;

    // Register event handlers
    /*
      If map moved, change the center and bounds, for so the address inputs
      can use to limit the suggestion based on current map view.
     */
    mapRef.current.on("moveend", () => {
      updateMapCenterAndBoundsInStore();
    });

    /*
     Handle click on the map
     */
    mapRef.current.on("click", event => {
      // Check if we hit something
      const pixel = mapRef.current.getEventPixel(event.originalEvent);
      const hit = mapRef.current.hasFeatureAtPixel(pixel);
      if (
        markersClusterSelectInteraction.getLayer().getSource().getFeatures()
          .length > 0
      ) {
        markersClusterSelectInteraction.clear();
      }
      if (hit) {
        // Handle driversLayer marker click
        const driversMarkersAtPixel = mapRef.current
          .getFeaturesAtPixel(pixel, {
            layerFilter: layerCandidate =>
              layerCandidate.get("name") === driversLayerRef.current.get("name")
          })
          .filter(feature => feature.getGeometry().getType() === "Point");
        if (driversMarkersAtPixel.length > 0) {
          const markerOnTop = utils.features.findMaxByZIndex(
            driversMarkersAtPixel
          );
          if (markerOnTop) {
            const { date, vehicleName, timeWindows, routeId } =
              markerOnTop.get("data");
            popupOverlayRef.current.show(
              markerOnTop.getGeometry().getFirstCoordinate(),
              `<div class='popup-content-wrapper'><div class='popup-header'>${vehicleName}</div><div class='popup-normal-content'>${timeWindows}</div><div class='popup-content'>Seen at ${parseDateStr(
                date
              )}</div></div>`
            );
            setDriverTrackRouteId(routeId);
            return;
          }
        }

        // Handle stop markers click
        const stopsLayerMarkersAtPixel = mapRef.current.getFeaturesAtPixel(
          pixel,
          {
            layerFilter: layerCandidate =>
              layerCandidate.get("name") ===
                stopMarkersLayerRef.current.get("name") ||
              layerCandidate.get("name") ===
                showingRouteMarkersLayerRef.current.get("name")
          }
        );
        if (stopsLayerMarkersAtPixel.length > 0) {
          const clusterMarker = stopsLayerMarkersAtPixel[0];
          const clusterFeatures = clusterMarker.get("features");
          if (
            mapRef.current.getView().getZoom() < CLUSTER_MAX_ZOOM_LEVEL &&
            clusterFeatures.length > 1
          ) {
            // Clicked on cluster marker
            const extent = createEmptyExtent();
            clusterFeatures.forEach(marker => {
              try {
                extendExtent(extent, marker.getGeometry().getExtent());
              } catch (error) {
                console.error("Error getting marker extent", error, marker);
              }
            });
            try {
              mapRef.current
                .getView()
                .fit(extent, { duration: 500, padding: [50, 50, 50, 50] });
            } catch (error) {
              console.error(
                "Error fitting extent to markers",
                error,
                clusterFeatures
              );
            }
          } else if (clusterFeatures.length === 1) {
            const marker = clusterFeatures[0];
            if (marker) {
              // Clicked on stop marker
              const { id, type, status } = marker.get("stopData");
              if (type === "start") {
                popupOverlayRef.current.show(
                  marker.getGeometry().getFirstCoordinate(),
                  "<div class='popup-content-wrapper'><div class='popup-header'>Route</div><div class='popup-content'>Start location</div></div>"
                );
              } else {
                warnClick(id, type, status);
              }
            }
          }
          return;
        }

        // Handle polyline click
        const polylinesAtPixel = mapRef.current
          .getFeaturesAtPixel(pixel, {
            layerFilter: layerCandidate =>
              layerCandidate.get("name") ===
              routePolylinesLayerRef.current.get("name")
          })
          .filter(feature => feature.getGeometry().getType() === "LineString");
        if (polylinesAtPixel.length > 0) {
          const polyline = polylinesAtPixel[0];
          if (polyline.getId() === internalStoreRef.current.showingRouteId) {
            setShowingRouteId("");
          } else {
            setShowingRouteId(polyline.getId());
          }
        }
      }
    });

    // Handle disabling cluster if zoomed to close in map
    mapRef.current.getView().on(
      "change:resolution",
      function () {
        if (mapRef.current.getView().getZoom() >= CLUSTER_MAX_ZOOM_LEVEL) {
          if (!markersClusterSelectInteraction.getActive()) {
            markersClusterSelectInteraction.setActive(true);
          }
          if (internalStoreRef.current.markersClustered) {
            stopMarkersLayerRef.current.getSource().setDistance(0);
          }
        } else {
          if (markersClusterSelectInteraction.getActive()) {
            markersClusterSelectInteraction.setActive(false);
          }
          if (internalStoreRef.current.markersClustered) {
            stopMarkersLayerRef.current
              .getSource()
              .setDistance(CLUSTERING_DISTANCE);
          }
        }
      },
      mapRef.current
    );

    // Hover event handlers
    // Stop markers layer, contains stop markers and cluster markers
    stopMarkersLayerHoverInteraction.on("enter", event => {
      const clusterFeature = event.feature;
      if (clusterFeature.getGeometry().getType() === "Point") {
        const clusterFeatureFeatures = clusterFeature.get("features");
        const hasClustering =
          internalStoreRef.current.markersClustered &&
          mapRef.current.getView().getZoom() < CLUSTER_MAX_ZOOM_LEVEL;
        if (hasClustering && clusterFeatureFeatures.length > 1) {
          // Hovered on cluster marker
          clusterSurfaceLayerRef.current.getSource().clear(true);
          const markersCoordinates = clusterFeatureFeatures.map(marker =>
            marker.getGeometry().getCoordinates()
          );
          const surfaceGeometryPolygon = new Polygon([
            ConvexHull(markersCoordinates)
          ]);
          if (surfaceGeometryPolygon.getArea() > 0) {
            const surface = new Feature({
              geometry: surfaceGeometryPolygon
            });
            surface.setStyle(utils.featureStyles.getClusterSurfaceStyle());
            clusterSurfaceLayerRef.current.getSource().addFeature(surface);
          }
        } else {
          const marker = clusterFeatureFeatures[0];
          if (marker) {
            // Do not hover over current in edit marker or in showing route
            if (
              internalStoreRef.current.currentEditId !== marker.getId() &&
              internalStoreRef.current.showingRouteId !== marker.get("routeId")
            ) {
              if (currentHoveredStopMarkersLayerFeatureRef.current) {
                const clusterFeatureFeatures =
                  currentHoveredStopMarkersLayerFeatureRef.current.get(
                    "features"
                  );
                if (clusterFeatureFeatures.length === 1) {
                  currentHoveredStopMarkersLayerFeatureRef.current.setStyle(
                    utils.layerStyles.getMarkerLayerFeatureStyle(
                      currentHoveredStopMarkersLayerFeatureRef.current,
                      "normal",
                      !hasClustering
                    )
                  );
                  currentHoveredStopMarkersLayerFeatureRef.current = null;
                }
                featureTextLayerRef.current.getSource().clear(true);
              }
              clusterFeature.setStyle(
                utils.layerStyles.getMarkerLayerFeatureStyle(
                  clusterFeature,
                  "hovered",
                  !hasClustering
                )
              );
              const markerLabel = marker.get("label");
              if (markerLabel) {
                const { lon, lat } = marker.get("stopData");
                const markerType = marker.get("markerType");
                const textFeature = new Feature({
                  geometry: new Point(utils.pointArray.lonLatToXY([lon, lat]))
                });
                textFeature.setStyle(
                  utils.featureStyles.getStopMarkerLabelStyle(
                    markerLabel,
                    markerType
                  )
                );
                featureTextLayerRef.current.getSource().addFeature(textFeature);
              }
              currentHoveredStopMarkersLayerFeatureRef.current = clusterFeature;
            }

            // Handle snap to road if enabled
            if (
              internalStoreRef.current.snapToRoadEnabled &&
              mapRef.current.getView().getZoom() > 15
            ) {
              const { lon, lat, streetLat, streetLon } = marker.get("stopData");
              const lineFeature = new Feature({
                geometry: new LineString([
                  utils.pointArray.lonLatToXY([lon, lat]),
                  utils.pointArray.lonLatToXY([streetLon, streetLat])
                ])
              });
              lineFeature.setStyle(
                utils.featureStyles.getSnapToRoadLineStyle()
              );
              const parkingIconFeature = new Feature({
                geometry: new Point(
                  utils.pointArray.lonLatToXY([streetLon, streetLat])
                )
              });
              parkingIconFeature.setStyle(
                utils.featureStyles.getParkingPointStyle()
              );
              snapToRoadLayerRef.current
                .getSource()
                .addFeatures([lineFeature, parkingIconFeature]);
            }
          }
        }
      }
    });

    stopMarkersLayerHoverInteraction.on("leave", () => {
      if (currentHoveredStopMarkersLayerFeatureRef.current) {
        const clusterFeatureFeatures =
          currentHoveredStopMarkersLayerFeatureRef.current.get("features");
        const hasClustering =
          internalStoreRef.current.markersClustered &&
          mapRef.current.getView().getZoom() < CLUSTER_MAX_ZOOM_LEVEL;
        if (!(hasClustering && clusterFeatureFeatures.length > 1)) {
          currentHoveredStopMarkersLayerFeatureRef.current.setStyle(
            utils.layerStyles.getMarkerLayerFeatureStyle(
              currentHoveredStopMarkersLayerFeatureRef.current,
              "normal",
              !hasClustering
            )
          );
          currentHoveredStopMarkersLayerFeatureRef.current = null;
        }
      }
      snapToRoadLayerRef.current.getSource().clear(true);
      clusterSurfaceLayerRef.current.getSource().clear(true);
      featureTextLayerRef.current.getSource().clear(true);
    });

    showingRouteMarkersLayerHoverInteraction.on("enter", event => {
      const clusterFeature = event.feature;
      if (clusterFeature.getGeometry().getType() === "Point") {
        const clusterFeatureFeatures = clusterFeature.get("features");
        if (clusterFeatureFeatures.length === 1) {
          const marker = clusterFeatureFeatures[0];
          if (marker) {
            // Do not hover over current in edit marker or in showing route
            if (internalStoreRef.current.currentEditId !== marker.getId()) {
              if (currentHoveredStopMarkersLayerFeatureRef.current) {
                const clusterFeatureFeatures =
                  currentHoveredStopMarkersLayerFeatureRef.current.get(
                    "features"
                  );
                if (clusterFeatureFeatures.length === 1) {
                  currentHoveredStopMarkersLayerFeatureRef.current.setStyle(
                    utils.layerStyles.getMarkerLayerFeatureStyle(
                      currentHoveredStopMarkersLayerFeatureRef.current,
                      "normal"
                    )
                  );
                  currentHoveredStopMarkersLayerFeatureRef.current = null;
                }
                featureTextLayerRef.current.getSource().clear(true);
              }
              clusterFeature.setStyle(
                utils.layerStyles.getMarkerLayerFeatureStyle(
                  clusterFeature,
                  "hovered"
                )
              );
              const markerLabel = marker.get("label");
              if (markerLabel) {
                const { lon, lat } = marker.get("stopData");
                const markerType = marker.get("markerType");
                const textFeature = new Feature({
                  geometry: new Point(utils.pointArray.lonLatToXY([lon, lat]))
                });
                textFeature.setStyle(
                  utils.featureStyles.getStopMarkerLabelStyle(
                    markerLabel,
                    markerType
                  )
                );
                featureTextLayerRef.current.getSource().addFeature(textFeature);
              }
              currentHoveredShowingRouteMarkersLayerFeatureRef.current =
                clusterFeature;
            }

            // Handle snap to road if enabled
            if (
              internalStoreRef.current.snapToRoadEnabled &&
              mapRef.current.getView().getZoom() > 15
            ) {
              const { lon, lat, streetLat, streetLon } = marker.get("stopData");
              const lineFeature = new Feature({
                geometry: new LineString([
                  utils.pointArray.lonLatToXY([lon, lat]),
                  utils.pointArray.lonLatToXY([streetLon, streetLat])
                ])
              });
              lineFeature.setStyle(
                utils.featureStyles.getSnapToRoadLineStyle()
              );
              const parkingIconFeature = new Feature({
                geometry: new Point(
                  utils.pointArray.lonLatToXY([streetLon, streetLat])
                )
              });
              parkingIconFeature.setStyle(
                utils.featureStyles.getParkingPointStyle()
              );
              snapToRoadLayerRef.current
                .getSource()
                .addFeatures([lineFeature, parkingIconFeature]);
            }
          }
        }
      }
    });

    showingRouteMarkersLayerHoverInteraction.on("leave", () => {
      if (currentHoveredShowingRouteMarkersLayerFeatureRef.current) {
        currentHoveredShowingRouteMarkersLayerFeatureRef.current.setStyle(
          utils.layerStyles.getMarkerLayerFeatureStyle(
            currentHoveredShowingRouteMarkersLayerFeatureRef.current,
            "normal"
          )
        );
        currentHoveredShowingRouteMarkersLayerFeatureRef.current = null;
      }
      snapToRoadLayerRef.current.getSource().clear(true);
      featureTextLayerRef.current.getSource().clear(true);
    });

    setMapCreated(true);
  };

  const drawOnMap = useCallback(
    (routes, unscheduledStops, theSolution, thePolylines, refit = false) => {
      let markers = [];

      // Handle routes stops
      if (!internalStoreRef.current.markersHidden && routes.length > 0) {
        routes.forEach(route => {
          markers = markers.concat(
            getRouteStopMarkers(route.stops, route.id, theSolution.routeColors)
          );
        });
      }
      // Add unscheduled stops marker
      if (!internalStoreRef.current.unscheduledMarkersHidden) {
        (theSolution && theSolution.unassigned
          ? theSolution.unassigned.stops
          : unscheduledStops
        ).forEach(stop => {
          const marker = new Feature({
            geometry: new Point(
              utils.pointArray.lonLatToXY([stop.address.lng, stop.address.lat])
            ),
            markerType: "unscheduled",
            label: stop.information ? stop.information.street : "",
            stopData: {
              id: stop.id,
              type: stop.type,
              status: stop.status,
              lon: stop.address.lng,
              lat: stop.address.lat,
              streetLon: stop.address.lng_str || stop.address.lng || "",
              streetLat: stop.address.lat_str || stop.address.lat || ""
            }
          });
          marker.setId(stop.id);
          markers.push(marker);
        });
      }

      stopMarkersLayerRef.current.getSource().getSource().clear(true);
      if (markers.length > 0) {
        stopMarkersLayerRef.current
          .getSource()
          .getSource()
          .addFeatures(markers);
      }

      // Handle routes routes
      const createdPolylines = [];
      if (thePolylines && Object.keys(thePolylines).length > 0) {
        const normalizedRouteIds = routes.map(route => route.id);
        Object.keys(thePolylines)
          .filter(routeId => normalizedRouteIds.includes(routeId))
          .forEach(routeId => {
            const color = getRouteColor(theSolution.routeColors, routeId);
            const routeLinestring = new LineString(
              thePolylines[routeId].map(point => [point[1], point[0]])
            ).transform("EPSG:4326", "EPSG:3857");
            const polyline = new Feature({
              geometry: routeLinestring,
              color
            });
            polyline.setId(routeId);
            polyline.setStyle(
              routeId === internalStoreRef.current.hoveredRouteId
                ? utils.featureStyles.getHighlightedRoutePolylinesStyle(color)
                : utils.featureStyles.getRoutePolylinesStyle(color)
            );
            createdPolylines.push(polyline);
          });
      }

      routePolylinesLayerRef.current.getSource().clear(true);
      if (createdPolylines.length > 0) {
        routePolylinesLayerRef.current
          .getSource()
          .addFeatures(createdPolylines);
      }
      if (refit) {
        refitMarkersExtent(markers);
      }
    },
    [getRouteColor, getRouteStopMarkers, refitMarkersExtent]
  );

  // Effects
  // Add project stop markers to the map after initialization
  useEffect(() => {
    if (mapInitialized) {
      drawOnMap(
        normalizedRoutesWithNormalizedStops,
        services,
        solution,
        polylines,
        internalStoreRef.current.currentRefitHash !== refitHash
      );
      internalStoreRef.current.currentRefitHash = refitHash;
    }
  }, [
    drawOnMap,
    mapInitialized,
    normalizedRoutesWithNormalizedStops,
    solution,
    services,
    polylines,
    refitHash
  ]);

  // Markers visibility
  useEffect(() => {
    if (
      mapInitialized &&
      (internalStoreRef.current.markersHidden !== markersHidden ||
        internalStoreRef.current.unscheduledMarkersHidden !==
          unscheduledMarkersHidden)
    ) {
      internalStoreRef.current.markersHidden = markersHidden;
      internalStoreRef.current.unscheduledMarkersHidden =
        unscheduledMarkersHidden;
      drawOnMap(
        normalizedRoutesWithNormalizedStops,
        services,
        solution,
        polylines,
        false
      );
    }
  }, [
    drawOnMap,
    mapInitialized,
    markersHidden,
    normalizedRoutesWithNormalizedStops,
    polylines,
    services,
    solution,
    unscheduledMarkersHidden
  ]);

  useEffect(() => {
    showingRouteMarkersLayerRef.current.getSource().getSource().clear(true);
    showingRoutePolylineLayerRef.current.getSource().clear(true);
    if (
      solution &&
      solution.routes &&
      showingRouteId &&
      solution.routes.find(route => route.id === showingRouteId) &&
      polylines
    ) {
      const color = getRouteColor(solution.routeColors, showingRouteId);
      const routePolyline = polylines[showingRouteId];
      if (routePolyline) {
        const routeLinestring = new LineString(
          routePolyline.map(point => [point[1], point[0]])
        ).transform("EPSG:4326", "EPSG:3857");
        const polyline = new Feature({
          geometry: routeLinestring,
          color
        });
        showingRoutePolylineLayerRef.current.getSource().addFeature(polyline);
      }

      const route = normalizedRoutesWithNormalizedStops.find(
        route => route.id === showingRouteId
      );
      if (route && route.stops.length > 2) {
        showingRouteMarkersLayerRef.current
          .getSource()
          .getSource()
          .addFeatures(
            getRouteStopMarkers(
              route.stops,
              route.id,
              solution.routeColors,
              true
            )
          );
      }
    }
    internalStoreRef.current.showingRouteId = showingRouteId;
  }, [
    getRouteColor,
    getRouteStopMarkers,
    normalizedRoutesWithNormalizedStops,
    polylines,
    showingRouteId,
    solution
  ]);

  // Emphasise the polyline of the route whose card is hovered in the routes list.
  // Restyles only the affected feature(s) — cheap — restoring the previously
  // hovered one to its normal style. The style zIndex lifts the line above the
  // other (overlapping) routes so it's instantly clear which polyline it is.
  useEffect(() => {
    const layer = routePolylinesLayerRef.current;
    if (!layer) return;
    const source = layer.getSource();
    const prevId = internalStoreRef.current.hoveredRouteId;
    if (prevId && prevId !== hoveredRouteId) {
      const prev = source.getFeatureById(prevId);
      if (prev) {
        prev.setStyle(
          utils.featureStyles.getRoutePolylinesStyle(prev.get("color"))
        );
      }
    }
    if (hoveredRouteId) {
      const feature = source.getFeatureById(hoveredRouteId);
      if (feature) {
        feature.setStyle(
          utils.featureStyles.getHighlightedRoutePolylinesStyle(
            feature.get("color")
          )
        );
      }
    }
    internalStoreRef.current.hoveredRouteId = hoveredRouteId;
  }, [hoveredRouteId]);

  // Fit to editing marker
  useEffect(() => {
    if (editId) {
      hoveredMarkerLayerRef.current.getSource().clear(true);
      editMarkerLayerRef.current.getSource().clear(true);
      let marker = null;
      // Check if it's unassigned stop
      const unassignedStop = (
        solution ? solution.unassigned.stops : services
      ).find(stop => stop.id === editId);
      if (unassignedStop) {
        marker = new Feature({
          geometry: new Point(
            utils.pointArray.lonLatToXY([
              unassignedStop.address.lng,
              unassignedStop.address.lat
            ])
          ),
          markerType: "unscheduled",
          label: unassignedStop.information
            ? unassignedStop.information.street
            : "",
          stopData: {
            id: unassignedStop.id,
            type: unassignedStop.type,
            status: unassignedStop.status,
            lon: unassignedStop.address.lng,
            lat: unassignedStop.address.lat,
            streetLon:
              unassignedStop.address.lng_str ||
              unassignedStop.address.lng ||
              "",
            streetLat:
              unassignedStop.address.lat_str || unassignedStop.address.lat || ""
          }
        });
        marker.setId(unassignedStop.id);
        marker.setStyle(
          utils.featureStyles.getStopMarkerStyles(marker, "edit")
        );
      } else {
        // Not in unassigned stops, so check the routes
        let stopIndex = -1;
        const route = normalizedRoutesWithNormalizedStops.find(route => {
          const indexOfStop = route.stops.findIndex(stop => stop.id === editId);
          if (indexOfStop >= 0) {
            stopIndex = indexOfStop;
          }
          return indexOfStop >= 0;
        });
        if (route && route.stops && route.stops[stopIndex]) {
          const routeId = route.id;
          const color = getRouteColor(solution.routeColors, routeId);
          const stop = route.stops[stopIndex];
          marker = new Feature({
            geometry: new Point(
              utils.pointArray.lonLatToXY([stop.address.lng, stop.address.lat])
            ),
            markerType: "stop",
            label: stop.information ? stop.information.street : "",
            routeId,
            stopData: {
              id: stop.id,
              type: stop.type,
              status: stop.status,
              lon: stop.address.lng,
              lat: stop.address.lat,
              streetLon: stop.address.lng_str || stop.address.lng || "",
              streetLat: stop.address.lat_str || stop.address.lat || "",
              index: stopIndex,
              color
            }
          });
          marker.setId(stop.id);
          marker.setStyle(
            utils.featureStyles.getStopMarkerStyles(marker, "edit")
          );
        }
      }
      if (marker) {
        const markerLabel = marker.get("label");
        if (markerLabel) {
          const { lon, lat } = marker.get("stopData");
          const markerType = marker.get("markerType");
          const textFeature = new Feature({
            geometry: new Point(utils.pointArray.lonLatToXY([lon, lat]))
          });
          textFeature.setStyle(
            utils.featureStyles.getStopMarkerLabelStyle(markerLabel, markerType)
          );
          editMarkerLayerRef.current.getSource().addFeature(textFeature);
        }
        editMarkerLayerRef.current.getSource().addFeature(marker);
        // Validate geometry before fitting view
        // Sometimes the marker geometry is not valid
        // and extent is not an array of numbers
        // so we need to check it before fitting the view
        // to avoid errors in the console and crashes
        try {
          const extent = marker.getGeometry().getExtent();
          if (
            Array.isArray(extent) &&
            extent.every(item => typeof item === "number" && isFinite(item))
          ) {
            mapRef.current.getView().fit(marker.getGeometry().getExtent(), {
              duration: 500,
              padding: [50, 50, 50, 600]
            });
          }
        } catch (error) {
          console.error("Error fitting to edit marker", error, marker);
        }
      }
    } else {
      editMarkerLayerRef.current.getSource().clear(true);
    }
    internalStoreRef.current.currentEditId = editId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, solution, normalizedRoutesWithNormalizedStops, services]);

  // Handle hovered stop marker
  useEffect(() => {
    if (hoveredStop && hoveredStop.stop) {
      hoveredMarkerLayerRef.current.getSource().clear(true);
      let marker;

      const unassignedStop = (
        solution ? solution.unassigned.stops : services
      ).find(stop => stop.id === hoveredStop.stop.id);
      if (unassignedStop) {
        marker = new Feature({
          geometry: new Point(
            utils.pointArray.lonLatToXY([
              unassignedStop.address.lng,
              unassignedStop.address.lat
            ])
          ),
          markerType: "unscheduled",
          label: unassignedStop.information
            ? unassignedStop.information.street
            : "",
          stopData: {
            id: unassignedStop.id,
            type: unassignedStop.type,
            status: unassignedStop.status,
            lon: unassignedStop.address.lng,
            lat: unassignedStop.address.lat,
            streetLon:
              unassignedStop.address.lng_str ||
              unassignedStop.address.lng ||
              "",
            streetLat:
              unassignedStop.address.lat_str || unassignedStop.address.lat || ""
          }
        });
        marker.setId(unassignedStop.id);
        marker.setStyle(
          utils.featureStyles.getStopMarkerStyles(marker, "hovered")
        );
      } else {
        const { stop, routeId } = hoveredStop;
        const route = normalizedRoutesWithNormalizedStops.find(
          route => route.id === routeId
        );
        if (route) {
          const color = getRouteColor(solution.routeColors, routeId);
          const stopIndex = route.stops.findIndex(
            theStop => theStop.id === stop.id
          );
          marker = new Feature({
            geometry: new Point(
              utils.pointArray.lonLatToXY([stop.address.lng, stop.address.lat])
            ),
            markerType: "stop",
            label: stop.information ? stop.information.street : "",
            routeId,
            stopData: {
              id: stop.id,
              type: stop.type,
              status: stop.status,
              lon: stop.address.lng,
              lat: stop.address.lat,
              streetLon: stop.address.lng_str || stop.address.lng || "",
              streetLat: stop.address.lat_str || stop.address.lat || "",
              index: stopIndex,
              color
            }
          });
          marker.setId(stop.id);
          marker.setStyle(
            utils.featureStyles.getStopMarkerStyles(marker, "hovered")
          );
        }
      }

      if (marker) {
        const markerLabel = marker.get("label");
        if (markerLabel) {
          const { lon, lat } = marker.get("stopData");
          const markerType = marker.get("markerType");
          const textFeature = new Feature({
            geometry: new Point(utils.pointArray.lonLatToXY([lon, lat]))
          });
          textFeature.setStyle(
            utils.featureStyles.getStopMarkerLabelStyle(markerLabel, markerType)
          );
          hoveredMarkerLayerRef.current.getSource().addFeature(textFeature);
        }
        hoveredMarkerLayerRef.current.getSource().addFeature(marker);
      }
    }
    if (!hoveredStop) {
      hoveredMarkerLayerRef.current.getSource().clear(true);
    }
  }, [
    getRouteColor,
    hoveredStop,
    normalizedRoutesWithNormalizedStops,
    services,
    solution
  ]);

  // Handle the pinned (locked RouteTag) stop marker — highlight it on the map the
  // same way a hovered unscheduled stop is, but persistently while it's pinned.
  useEffect(() => {
    lockedMarkerLayerRef.current.getSource().clear(true);
    const stop =
      lockedMatchStopId && solution
        ? solution.unassigned.stops.find(s => s.id === lockedMatchStopId)
        : null;
    if (stop && stop.address) {
      const marker = new Feature({
        geometry: new Point(
          utils.pointArray.lonLatToXY([stop.address.lng, stop.address.lat])
        ),
        markerType: "unscheduled",
        label: stop.information ? stop.information.street : "",
        stopData: {
          id: stop.id,
          type: stop.type,
          status: stop.status,
          lon: stop.address.lng,
          lat: stop.address.lat,
          streetLon: stop.address.lng_str || stop.address.lng || "",
          streetLat: stop.address.lat_str || stop.address.lat || ""
        }
      });
      marker.setId(stop.id);
      marker.setStyle(
        utils.featureStyles.getStopMarkerStyles(marker, "hovered")
      );
      const markerLabel = marker.get("label");
      if (markerLabel) {
        const { lon, lat } = marker.get("stopData");
        const textFeature = new Feature({
          geometry: new Point(utils.pointArray.lonLatToXY([lon, lat]))
        });
        textFeature.setStyle(
          utils.featureStyles.getStopMarkerLabelStyle(markerLabel, "unscheduled")
        );
        lockedMarkerLayerRef.current.getSource().addFeature(textFeature);
      }
      lockedMarkerLayerRef.current.getSource().addFeature(marker);
    }
  }, [lockedMatchStopId, solution]);

  // Drivers location
  useEffect(() => {
    if (mapInitialized && publishedOnce && driverLocations) {
      driversLayerRef.current.getSource().clear(true);
      if (driverLocations) {
        Object.keys(driverLocations).forEach(routeId => {
          const route = normalizedRoutesWithNormalizedStops.find(
            route => route.id === routeId
          );
          if (route) {
            const { lng: lon, lat, date } = driverLocations[routeId];
            const color = getRouteColor(solution.routeColors, routeId);
            const vehicle = vehicles.find(
              vehicle => vehicle?.vehicle_id === route.vehicle_id
            );
            const vehicleName = vehicle ? vehicle?.type_id?.name : "";
            const startEndStops = route.stops.filter(
              stop => stop.type === "start" || stop.type === "end"
            );
            const startTime =
              startEndStops[0]?.finish_time || startEndStops[0]?.end_time;
            const endTime = startEndStops[startEndStops.length - 1]?.arr_time;
            const marker = new Feature({
              geometry: new Point(utils.pointArray.lonLatToXY([lon, lat])),
              data: {
                timeWindows:
                  startTime && endTime
                    ? `${secondsToTime(startTime)} - ${secondsToTime(endTime)}`
                    : "",
                lon,
                lat,
                date,
                vehicleName,
                routeId
              }
            });
            marker.setStyle(utils.featureStyles.getDriverMarkerStyle(color));
            driversLayerRef.current.getSource().addFeature(marker);
          }
        });
      }
    }
  }, [
    vehicles,
    mapInitialized,
    driverLocations,
    getRouteColor,
    solution,
    normalizedRoutesWithNormalizedStops,
    publishedOnce
  ]);

  // Track current driver with opened popup
  useEffect(() => {
    if (
      mapInitialized &&
      publishedOnce &&
      driverTrackRouteId &&
      driverLocations[driverTrackRouteId]
    ) {
      const { lng: lon, lat } = driverLocations[driverTrackRouteId];
      mapRef.current
        .getView()
        .centerOn(
          utils.pointArray.lonLatToXY([lon, lat]),
          mapRef.current.getSize(),
          [500, 570]
        );
    }
  }, [driverLocations, driverTrackRouteId, mapInitialized, publishedOnce]);

  // Load map style (dark/light).
  useEffect(() => {
    if (mapCreated && plan.default_map_tile_provider) {
      // Change MAP_USE_RASTER to false value to use vector tiles.
      if (MAP_USE_RASTER) {
        mapRef.current
          .getLayerGroup()
          .getLayersArray()
          .filter(layer => layer instanceof TileLayer)
          .forEach(layer => mapRef.current.removeLayer(layer));
        // Raster tiles. Way much better performance
        mapRef.current.addLayer(
          new TileLayer({
            source: new XYZ({
              attributions: `&copy; ${new Date().getFullYear()} <a href="/">Routetitan</a>, &copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors`,
              tilePixelRatio: Math.min(Math.ceil(DEVICE_PIXEL_RATIO), 2),
              url: utils.mapTiles.getUrl(
                plan,
                tilesDark,
                Math.min(Math.ceil(DEVICE_PIXEL_RATIO), 2)
              )
            })
          })
        );
        setMapInitialized(true);
      } else {
        // Vector tiles. Not as good as raster regarding the performance.
        /*
          Clear previously added layers by olMapboxStyleApply before applying
          new style.
         */
        mapRef.current
          .getLayerGroup()
          .getLayersArray()
          .filter(layer => layer.get("mapbox-source"))
          .forEach(layer => mapRef.current.removeLayer(layer));
        olMapboxStyleApply(
          mapRef.current,
          utils.mapTiles.getUrl(plan, tilesDark)
        )
          .then(function () {
            setMapInitialized(true);
          })
          .catch(error => {
            console.log(error);
          });
      }
    }
  }, [mapCreated, plan, tilesDark]);

  // Set the user country as initial view
  useEffect(() => {
    if (organisation && mapInitialized && !organisationLoaded) {
      const coordinates = countryLocations[organisation.country];
      if (coordinates) {
        const { sw_lat, sw_lng, ne_lat, ne_lng } = coordinates;
        mapRef.current
          .getView()
          .fit(utils.extentArray.lonLatToXY([sw_lng, sw_lat, ne_lng, ne_lat]), {
            callback() {
              updateMapCenterAndBoundsInStore();
              setOrganisationLoaded(true);
            }
          });
      }
    }
  }, [
    mapInitialized,
    organisation,
    organisationLoaded,
    updateMapCenterAndBoundsInStore
  ]);

  // Disable or enable the cluster
  useEffect(() => {
    if (mapCreated) {
      if (markersClustered) {
        stopMarkersLayerRef.current
          .getSource()
          .setDistance(CLUSTERING_DISTANCE);
      } else {
        stopMarkersLayerRef.current.getSource().setDistance(0);
      }
      internalStoreRef.current.markersClustered = markersClustered;
    }
  }, [mapCreated, markersClustered]);

  // If project changed, or have no project, clear everything
  useEffect(() => {
    if (mapCreated && (!projectId || projectId !== matchedProjectRef.current)) {
      stopMarkersLayerRef.current.getSource().getSource().clear(true);
      clusterSurfaceLayerRef.current.getSource().clear(true);
      routePolylinesLayerRef.current.getSource().clear(true);
      snapToRoadLayerRef.current.getSource().clear(true);
      driversLayerRef.current.getSource().clear(true);
      editMarkerLayerRef.current.getSource().clear(true);
      featureTextLayerRef.current.getSource().clear(true);
      showingRouteMarkersLayerRef.current.getSource().getSource().clear(true);
      setDriverTrackRouteId(null);
      internalStoreRef.current = internalStoreInitialValue;
    }
  }, [mapCreated, projectId]);

  // Handle saveDisabled changes
  useEffect(() => {
    internalStoreRef.current.saveDisabled = saveDisabled;
  }, [saveDisabled]);

  // Handle snapToRoad changes
  useEffect(() => {
    internalStoreRef.current.snapToRoadEnabled = snapToRoadEnabled;
  }, [snapToRoadEnabled]);

  // Fetch routes for drawing polylines
  useEffect(() => {
    if (mapCreated && projectId && normalizedRoutes) {
      fetchRoutes(normalizedRoutes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCreated, projectId, normalizedRoutes]);

  return {
    mapRef,
    refitMarkersExtent,
    createMap,
    changesDialog,
    setChangesDialog,
    handleClick
  };
};

export default useMapHooks;
